import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import {
  Upload, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle,
  RefreshCw, Database, FileSpreadsheet, MapPin, Play, History, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['Select Target', 'Upload File', 'Map Fields', 'Review & Import'];

export default function MigrationWizardPage() {
  const [step, setStep] = useState(0);
  const [targets, setTargets] = useState([]);
  const [sources] = useState(['csv_upload', 'zoho_crm', 'quickbooks', 'salesforce', 'hubspot', 'other']);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [selectedSource, setSelectedSource] = useState('csv_upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('wizard');

  const fetchData = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([
        axios.get(`${API}/api/migration/targets`),
        axios.get(`${API}/api/migration/history`),
      ]);
      setTargets(t.data.targets || []);
      setMigrations(h.data.migrations || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Quick Data Import requires Growth or Enterprise plan');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    if (!file || !selectedTarget) { toast.error('Select a target and upload a file'); return; }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target', selectedTarget);
    try {
      const res = await axios.post(`${API}/api/migration/upload-preview`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      const autoMap = {};
      for (const tf of res.data.target_fields) {
        const match = res.data.source_columns.find(sc => sc.toLowerCase().replace(/[\s_-]/g, '') === tf.toLowerCase().replace(/[\s_-]/g, ''));
        if (match) autoMap[match] = tf;
      }
      setMapping(autoMap);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/migration/execute`, { session_id: preview.session_id, mapping });
      setResult(res.data);
      setStep(3);
      fetchData();
      toast.success(`Imported ${res.data.imported} records!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const reset = () => {
    setStep(0); setFile(null); setPreview(null); setMapping({}); setResult(null);
    setSelectedTarget(''); setSelectedSource('csv_upload');
  };

  if (loading) return <div className="flex justify-center p-12"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quick-data-import-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
          Quick Data Import<PageInfoTooltip pageKey="migration-wizard" />
        </h1>
        <p className="text-gray-medium text-sm mt-1">
          One-shot CSV / JSON / API imports into a single Konnect module (Leads, Contacts, etc).
          For full-blown legacy-to-cloud migrations use <a href="/migration-studio" className="text-emerald-600 hover:underline">Migration Studio</a> instead.
        </p>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === 'wizard' ? 'default' : 'outline'} size="sm" onClick={() => setTab('wizard')} data-testid="tab-wizard">
          <Upload className="h-3.5 w-3.5 mr-1.5" />Import Wizard
        </Button>
        <Button variant={tab === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('history')} data-testid="tab-migration-history">
          <History className="h-3.5 w-3.5 mr-1.5" />History
        </Button>
      </div>

      {tab === 'wizard' && (
        <>
          {/* Stepper */}
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="stepper">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-2 flex-shrink-0">
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors",
                  i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
                )}>
                  {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                <span className={cn("text-xs whitespace-nowrap", i === step ? "text-text-primary font-medium" : "text-gray-400")}>{s}</span>
                {i < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-gray-300 flex-shrink-0" />}
              </div>
            ))}
          </div>

          {/* Step 0: Select target & source */}
          {step === 0 && (
            <Card className="bg-white border-gray-border">
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>What are you importing?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {targets.map(t => (
                      <button key={t.key} onClick={() => setSelectedTarget(t.key)}
                        className={cn("p-3 rounded-lg border text-left transition-all text-sm",
                          selectedTarget === t.key ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" : "border-gray-border hover:border-gray-300"
                        )} data-testid={`target-${t.key}`}>
                        <Database className="h-4 w-4 mb-1 text-blue-600" />
                        <p className="font-medium text-xs">{t.label}</p>
                        <p className="text-[10px] text-gray-400">{t.required_fields.length} required fields</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Source System</Label>
                  <Select value={selectedSource} onValueChange={setSelectedSource}>
                    <SelectTrigger className="mt-1" data-testid="source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sources.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => { if (!selectedTarget) { toast.error('Select a target'); return; } setStep(1); }} disabled={!selectedTarget} data-testid="next-step-1">
                    Next<ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Upload */}
          {step === 1 && (
            <Card className="bg-white border-gray-border">
              <CardContent className="pt-6 space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-gray-medium mb-3">Upload a CSV file with your {targets.find(t => t.key === selectedTarget)?.label || ''} data</p>
                  <Input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="max-w-xs mx-auto" data-testid="file-input" />
                  {file && <p className="text-xs text-emerald-600 mt-2"><FileSpreadsheet className="h-3 w-3 inline mr-1" />{file.name}</p>}
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(0)} data-testid="back-step-0"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                  <Button onClick={handleUpload} disabled={!file} data-testid="upload-btn"><Upload className="h-4 w-4 mr-1" />Upload & Preview</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Map fields */}
          {step === 2 && preview && (
            <Card className="bg-white border-gray-border">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-sm">{preview.filename}</p>
                    <p className="text-xs text-gray-medium">{preview.total_rows} rows found · {preview.source_columns.length} columns</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">{preview.target_fields.length} target fields</Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Field Mapping</p>
                  {preview.target_fields.map(tf => {
                    const isRequired = preview.required_fields.includes(tf);
                    const mappedCol = Object.entries(mapping).find(([, v]) => v === tf)?.[0] || '';
                    return (
                      <div key={tf} className="flex items-center gap-3 p-2 rounded-lg border border-gray-border">
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium capitalize">{tf.replace(/_/g, ' ')}</span>
                          {isRequired && <Badge className="ml-1.5 bg-red-100 text-red-600 text-[8px]">Required</Badge>}
                        </div>
                        <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        <Select value={mappedCol} onValueChange={v => {
                          const updated = { ...mapping };
                          Object.keys(updated).forEach(k => { if (updated[k] === tf) delete updated[k]; });
                          if (v) updated[v] = tf;
                          setMapping(updated);
                        }}>
                          <SelectTrigger className="w-44 h-8" data-testid={`map-${tf}`}><SelectValue placeholder="Select column" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">-- None --</SelectItem>
                            {preview.source_columns.map(sc => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>

                {preview.preview_rows.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Preview (first {preview.preview_rows.length} rows)</p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-gray-50">{preview.source_columns.map(c => <th key={c} className="px-2 py-1.5 text-left font-medium text-gray-600">{c}</th>)}</tr></thead>
                        <tbody>{preview.preview_rows.map((row, i) => <tr key={i} className="border-t">{preview.source_columns.map(c => <td key={c} className="px-2 py-1.5 text-gray-700 truncate max-w-[120px]">{row[c]}</td>)}</tr>)}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="back-step-1"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                  <Button onClick={handleImport} disabled={importing} data-testid="import-btn">
                    {importing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                    Import {preview.total_rows} Records
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Result */}
          {step === 3 && result && (
            <Card className="bg-white border-gray-border">
              <CardContent className="pt-6">
                <div className="text-center py-6">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
                  <h2 className="text-xl font-bold text-text-primary mb-2">Migration Complete!</h2>
                  <div className="flex justify-center gap-6 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{result.imported}</p>
                      <p className="text-xs text-gray-medium">Imported</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-500">{result.skipped}</p>
                      <p className="text-xs text-gray-medium">Skipped</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-400">{result.total_rows}</p>
                      <p className="text-xs text-gray-medium">Total Rows</p>
                    </div>
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="text-left bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4 max-w-md mx-auto">
                      <p className="text-xs font-medium text-amber-800 mb-1"><AlertTriangle className="h-3 w-3 inline mr-1" />Warnings:</p>
                      {result.errors.map((e, i) => <p key={i} className="text-[10px] text-amber-700">{e}</p>)}
                    </div>
                  )}
                  <Button onClick={reset} className="mt-6" data-testid="new-migration-btn">
                    <Upload className="h-4 w-4 mr-1" />Start New Migration
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {tab === 'history' && (
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4">
            {migrations.length === 0 ? (
              <div className="text-center py-12 text-gray-medium">
                <History className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No migrations yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {migrations.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`migration-row-${i}`}>
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                      m.status === 'completed' ? "bg-emerald-50" : "bg-amber-50"
                    )}>
                      {m.status === 'completed' ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <RefreshCw className="h-4 w-4 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-text-primary capitalize">{m.target?.replace(/_/g, ' ')}</span>
                        <Badge className={cn("text-[10px]", m.status === 'completed' ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{m.status}</Badge>
                        {m.imported > 0 && <Badge className="bg-blue-100 text-blue-600 text-[10px]">{m.imported} imported</Badge>}
                      </div>
                      <p className="text-xs text-gray-medium">{m.filename} · by {m.created_by} · {m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
